import { requestJson } from '../api/http';
import type { Page } from '../api/pagination';
import {
  discardResponseSchema,
  notesPageSchema,
  restoreResponseSchema,
  retryResponseSchema,
  type DiscardResponse,
  type NoteSummary,
  type RestoreResponse,
  type RetryResponse,
} from '../notes/schema';
import type { NoteStatus } from '../notes/status';

export interface FetchNotesParams {
  /** The previous page's `next_cursor`, echoed back verbatim. `undefined` asks for the first
   * page - never build or parse this string (`app/api/pagination.ts`). */
  readonly cursor?: string | undefined;
  readonly limit?: number;
  /** `undefined` means every status; a single value narrows to one lifecycle state. */
  readonly status?: NoteStatus | undefined;
  /** Ask for the notes the owner discarded instead of their live ones. The backend never mixes
   * the two in one page, so this is a switch and not an inclusion flag. */
  readonly discarded?: boolean;
}

/** `GET /notes`, one page, newest first. */
export function fetchNotes({
  cursor,
  limit,
  status,
  discarded,
}: FetchNotesParams): Promise<Page<NoteSummary>> {
  const search = new URLSearchParams();
  if (cursor !== undefined) {
    search.set('cursor', cursor);
  }
  if (limit !== undefined) {
    search.set('limit', String(limit));
  }
  if (status !== undefined) {
    search.set('status', status);
  }
  if (discarded === true) {
    search.set('discarded', 'true');
  }
  const query = search.toString();
  return requestJson(`/notes${query ? `?${query}` : ''}`, notesPageSchema);
}

/** `POST /notes/{id}/retry` - only ever meaningful from a `failed` note (`app/api/retry.py`).
 * `201` means the failure was cleared and a new recognition job was durably queued; every
 * refusal (`401`/`403`/`404`/`409`/`500`) surfaces as the `ApiError` it already is, for the
 * caller to classify (`history/retryClassification.ts`). */
export function retryNote(noteId: string): Promise<RetryResponse> {
  return requestJson(`/notes/${noteId}/retry`, retryResponseSchema, { method: 'POST' });
}

/** `POST /notes/{id}/discard` - the owner throwing a note away (`app/api/discard.py`).
 * Nothing is deleted: the note leaves every list and stops owing work, and `POST
 * /notes/{id}/restore` undoes it. `409` means the note is already in Excel, already discarded,
 * or being delivered right now. */
export function discardNote(noteId: string, reason?: string): Promise<DiscardResponse> {
  const trimmed = reason?.trim() ?? '';
  return requestJson(`/notes/${noteId}/discard`, discardResponseSchema, {
    method: 'POST',
    body: trimmed === '' ? {} : { reason: trimmed },
  });
}

/** `POST /notes/{id}/restore` - bringing a discarded note back where it stood. */
export function restoreNote(noteId: string): Promise<RestoreResponse> {
  return requestJson(`/notes/${noteId}/restore`, restoreResponseSchema, { method: 'POST' });
}
